import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { COL_COLORS, COL_LABELS, STATUS, terbilang } from "../lib/format";

export const cn = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
const variants: Record<Variant, string> = {
  primary: "bg-primary text-primary-foreground hover:brightness-110 shadow-sm",
  secondary: "bg-secondary text-secondary-foreground hover:bg-muted",
  ghost: "bg-transparent hover:bg-muted text-foreground",
  danger: "bg-red-600 text-white hover:bg-red-700",
  outline: "border bg-card hover:bg-muted",
};
const sizes = { sm: "h-8 px-3 text-xs", md: "h-10 px-4 text-sm", icon: "h-9 w-9" };

export function Button({ variant = "primary", size = "md", className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: keyof typeof sizes }) {
  return (
    <button
      className={cn("inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-[background-color,transform,filter] duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none", variants[variant], sizes[size], className)}
      {...props}
    />
  );
}

export const Input = ({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) => <input className={cn("yt-input", className)} {...p} />;

export function MoneyInput({ value, onChange, className, placeholder, terbilang: tb, ...p }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> & { value: string | number | null | undefined; onChange: (digits: string) => void; terbilang?: boolean }) {
  const digits = String(value ?? "").replace(/\D/g, "");
  const shown = digits ? Number(digits).toLocaleString("id-ID") : "";
  const ph = placeholder && /^\d+$/.test(placeholder) ? Number(placeholder).toLocaleString("id-ID") : placeholder;
  return (
    <div>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">Rp</span>
        <input {...p} type="text" inputMode="numeric" autoComplete="off" className={cn("yt-input pl-9 num", className)} placeholder={ph} value={shown} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} />
      </div>
      {tb && digits && <p className="mt-1 text-xs italic text-primary first-letter:uppercase" data-testid={p["data-testid" as keyof typeof p] ? `${p["data-testid" as keyof typeof p]}-terbilang` : undefined}>{terbilang(Number(digits))}</p>}
    </div>
  );
}
export const Select = ({ className, children, ...p }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select className={cn("yt-input", className)} {...p}>
    {children}
  </select>
);
export const Textarea = ({ className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className={cn("yt-input h-auto py-2 min-h-[80px]", className)} {...p} />;

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function Card({ title, subtitle, action, children, className, testId }: { title?: ReactNode; subtitle?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; testId?: string }) {
  return (
    <section data-testid={testId} className={cn("card-surface rise", className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div>
            {title && <h3 className="text-base font-semibold">{title}</h3>}
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      <div className="px-5 pb-5 pt-2">{children}</div>
    </section>
  );
}

export function StatusBadge({ status, testId }: { status: string; testId?: string }) {
  const s = STATUS[status] || { label: status, cls: "bg-muted text-foreground border", dot: "bg-slate-400" };
  return (
    <span data-testid={testId || `status-badge-${status}`} className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap", s.cls)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
      {s.label}
    </span>
  );
}

export function ColBadge({ col }: { col: number }) {
  return (
    <span data-testid={`col-badge-${col}`} className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-bold text-white whitespace-nowrap" style={{ background: COL_COLORS[col] }}>
      Col {col} · {COL_LABELS[col]}
    </span>
  );
}

export function Modal({ open, onClose, title, children, wide, testId }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean; testId?: string }) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          data-testid={testId}
          aria-describedby={undefined}
          className={cn("fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto data-[state=open]:animate-in data-[state=open]:zoom-in-95", wide ? "max-w-3xl" : "max-w-lg")}
        >
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
            <Dialog.Close data-testid="modal-close-button" className="rounded-md p-1.5 hover:bg-muted transition-colors">
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, tone = "default", testId }: { label: string; value: ReactNode; hint?: ReactNode; tone?: "default" | "red" | "amber" | "green" | "blue"; testId?: string }) {
  const tones = {
    default: "border-border",
    red: "border-red-300 dark:border-red-800 bg-red-50/60 dark:bg-red-950/30",
    amber: "border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/30",
    green: "border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30",
    blue: "border-blue-300 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30",
  };
  return (
    <div data-testid={testId} className={cn("card-surface rise p-4", tones[tone])}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1.5 text-xl sm:text-2xl font-bold num">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function Empty({ text }: { text: string }) {
  return <div className="py-10 text-center text-sm text-muted-foreground">{text}</div>;
}

export function Confirm({ open, onClose, onConfirm, title, text, danger, testId }: { open: boolean; onClose: () => void; onConfirm: () => void; title: string; text: string; danger?: boolean; testId?: string }) {
  return (
    <Modal open={open} onClose={onClose} title={title} testId={testId}>
      <p className="text-sm text-muted-foreground">{text}</p>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} data-testid="confirm-cancel-button">Batal</Button>
        <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} data-testid="confirm-ok-button">Ya, lanjutkan</Button>
      </div>
    </Modal>
  );
}
